import { useState } from "react";
import { motion } from "framer-motion";
import ThankYou from "./ThankYou";
import Modal from "./Modal";
import { TermsContent, PrivacyContent } from "./Footer";

const init = { name: "", phone: "", message: "", consent: false };

const contactItems = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
      </svg>
    ),
    label: "Phone / SMS",
    value: "+1 435-288-3089",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    label: "Email",
    value: "info@superfastautospares.com",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    ),
    label: "Location",
    value: "1009 W 540 N Apt A\nSt. George, UT 84770\nUnited States",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    label: "Business Hours",
    value: "Mon – Sat: 9 AM to 7 PM",
  },
];

export default function Contact() {
  const [form, setForm] = useState(init);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [showThankYou, setShowThankYou] = useState(false);
  const [modal, setModal] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    else if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ""))) e.phone = "Enter a valid 10-digit US phone number.";
    if (!form.message.trim()) e.message = "Please describe what you need.";
    if (!form.consent) e.consent = "Consent is required to contact you.";
    return e;
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: "" }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus("loading");
    // Simulate SMS API call — replace with real Twilio / MSG91 integration
    await new Promise(r => setTimeout(r, 1600));
    setForm(init);
    setErrors({});
    setStatus("idle");
    setShowThankYou(true); // redirect to thank you page
  };

  if (showThankYou) {
    return <ThankYou onBack={() => setShowThankYou(false)} />;
  }

  const inputCls = (err) =>
    `w-full bg-gray-50 border ${err ? "border-red-400 bg-red-50/50" : "border-gray-200"}
     focus:border-red-500 focus:bg-white text-gray-900 placeholder-gray-400
     rounded-xl px-4 py-3.5 outline-none transition-all duration-200 text-sm`;

  const Field = ({ id, label, required, error, children }) => (
    <div>
      <label htmlFor={id} className="block text-gray-700 text-sm font-semibold mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1" role="alert">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );

  return (
    <>
    <section id="contact" className="py-28 bg-gray-50 relative overflow-hidden" aria-label="Contact">
      {/* Decorative blobs */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-red-50 rounded-full blur-3xl opacity-70 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }} className="text-center mb-14">
          <span className="chip mb-4">Get In Touch</span>
          <h2 className="text-[34px] sm:text-[40px] text-gray-900 mb-4 tracking-tight"
            style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800 }}>
            Send Us An <span className="text-red-600">Enquiry</span>
          </h2>
          <p className="text-gray-500 max-w-lg mx-auto text-[0.97rem] leading-relaxed">
            Tell us what part you need and we'll reply via <strong className="text-gray-700">SMS</strong> within
            minutes during business hours.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10 items-start">

          {/* ── Left info panel ── */}
          <motion.div initial={{ opacity: 0, x: -28 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="lg:col-span-2 space-y-5">

            {/* Contact card with background photo */}
            <div className="relative rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80')" }} />
              <div className="absolute inset-0 bg-gray-900/85" />
              <div className="relative z-10 p-8">
                <h3 className="text-white text-xl mb-6" style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700 }}>
                  Contact Information
                </h3>
                <div className="space-y-5">
                  {contactItems.map(item => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                        {item.icon}
                      </div>
                      <div>
                        <p className="text-white/45 text-xs mb-0.5">{item.label}</p>
                        <p className="text-white text-sm font-medium" style={{ whiteSpace: "pre-line" }}>{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </motion.div>

          {/* ── Form ── */}
          <motion.div initial={{ opacity: 0, x: 28 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="lg:col-span-3">
            <form onSubmit={onSubmit} noValidate
              className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/60 p-8 md:p-10 space-y-6">
              <div>
                <h3 className="text-gray-900 text-2xl mb-1" style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700 }}>
                  Send an Enquiry
                </h3>
                <p className="text-gray-400 text-sm">We reply by SMS within minutes on business days.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field id="name" label="Full Name"  error={errors.name}>
                  <input id="name" name="name" type="text" value={form.name} onChange={onChange}
                    placeholder="Your full name" className={inputCls(errors.name)} aria-required />
                </Field>
                <Field id="phone" label="Phone Number"  error={errors.phone}>
                  <div className="flex gap-2">
                    <span className="bg-gray-50 border border-gray-200 text-gray-500 text-sm px-3 py-3.5 rounded-xl font-medium flex-shrink-0">
                      +1
                    </span>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={onChange}
                      placeholder="(555) 000-0000" maxLength={14}
                      className={inputCls(errors.phone) + " flex-1"} aria-required />
                  </div>
                </Field>
              </div>

              <Field id="message" label="Your Message"  error={errors.message}>
                <textarea id="message" name="message" value={form.message} onChange={onChange}
                  placeholder="Describe the part you need — include your car make, model, and year…"
                  rows={4} className={inputCls(errors.message) + " resize-none"} aria-required />
              </Field>

              {/* Consent */}
              <div className={`rounded-2xl p-4 border transition-colors ${errors.consent ? "bg-red-50/60 border-red-200" : "bg-gray-50 border-gray-100"}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="consent" checked={form.consent} onChange={onChange}
                    className="mt-0.5 w-4 h-4 accent-red-600 flex-shrink-0" aria-required />
                  <span className="text-gray-600 text-sm leading-relaxed">
                    By providing your phone number and Submitting the form, you agree to receive transactional messages from {" "} <strong className="text-gray-900">Superfast Auto Parts</strong> {" "} . Msg freq varies.Msg & data rates may apply. Reply HELP for help and STOP to opt out. View our{" "}
                  <button type="button" onClick={() => setModal("terms")} className="underline text-gray-700 hover:text-red-600">Terms</button>
                  {" "}&amp;{" "}
                  <button type="button" onClick={() => setModal("privacy")} className="underline text-gray-700 hover:text-red-600">Privacy</button>.
                  </span>
                </label>
                {errors.consent && (
                  <p className="text-red-500 text-xs mt-2 ml-7" role="alert">{errors.consent}</p>
                )}
                {/* <p className="text-gray-400 text-xs mt-2 ml-7">
                  Your data is used only for this enquiry. We never sell your information.
                </p> */}
              </div>

              {/* Submit */}
              <button type="submit" disabled={status === "loading"}
                className={`w-full font-bold py-4 rounded-2xl text-base flex items-center justify-center gap-3 transition-all duration-200 ${status === "loading"
                    ? "bg-red-400 text-white cursor-wait"
                    : "bg-red-600 hover:bg-red-700 text-white shadow-xl shadow-red-600/25 hover:shadow-red-600/40 hover:-translate-y-0.5"
                  }`}
                style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700 }}
                aria-live="polite"
              >
                {status === "loading" && (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
                    <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {status === "loading" ? "Sending your enquiry…" : "Submit Enquiry →"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>

      <Modal isOpen={modal === "terms"} onClose={() => setModal(null)} title="Terms & Conditions">
        <TermsContent />
      </Modal>
      <Modal isOpen={modal === "privacy"} onClose={() => setModal(null)} title="Privacy Policy">
        <PrivacyContent />
      </Modal>
    </>
  );
}
